import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Pagination,
  MenuItem,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Search as SearchIcon, ViewList, ViewModule } from "@mui/icons-material";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { TitleTable } from "components/TitleTable";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import { debounce } from "lodash";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import { getTipologias, getDadosPaginacao } from "services/modules/tipologias";
import { ITipologia } from "shared/interfaces/tipologia";
import { getMeusCadastrosN2 } from "services/modules/cadastrosN2";
import { ICadastrosN2 } from "shared/interfaces/cadastrosN2";
import { useAuth } from "shared/hooks/auth";
import { getProjetistas } from "services/modules/projetistas";
import { IProjetista } from "shared/interfaces/projetista";
import configs from "Config";

// Função para construir a URL completa do croqui
const getCroquiUrl = (tipologia: ITipologia): string | null => {
  if (!tipologia.urlDesenhoSvg && !tipologia.nomeArquivoDesenho) {
    return null;
  }

  // Se já é uma URL completa (começa com http:// ou https://)
  if (tipologia.urlDesenhoSvg?.startsWith("http://") || tipologia.urlDesenhoSvg?.startsWith("https://")) {
    return tipologia.urlDesenhoSvg;
  }

  // Se contém referência ao endpoint da API (path parcial sem hostname)
  if (tipologia.urlDesenhoSvg) {
    const baseUrl = configs.REACT_APP_BASE_URL?.replace("/api/", "") || "";
    if (tipologia.urlDesenhoSvg.includes("?path=") || tipologia.urlDesenhoSvg.includes("/api/Pacote/imagem")) {
      return `${baseUrl}${tipologia.urlDesenhoSvg.startsWith("/") ? "" : "/"}${tipologia.urlDesenhoSvg}`;
    }
    // Caminho relativo (ex: assets/croquis/xxx.png) — rotear via endpoint da API
    const encodedPath = encodeURIComponent(tipologia.urlDesenhoSvg);
    return `${baseUrl}/api/Pacote/imagem?path=${encodedPath}`;
  }

  // Se só tem nome do arquivo, construir URL via endpoint da API
  if (tipologia.nomeArquivoDesenho) {
    const baseUrl = configs.REACT_APP_BASE_URL?.replace("/api/", "") || "";
    const encodedPath = encodeURIComponent(`assets/croquis/${tipologia.nomeArquivoDesenho}`);
    return `${baseUrl}/api/Pacote/imagem?path=${encodedPath}`;
  }

  return null;
};

const decodeLegacyText = (value?: string | null): string => {
  if (!value) return "";
  const hasHighCodePoint = Array.from(value).some(
    (char) => char.charCodeAt(0) > 255
  );
  if (hasHighCodePoint) {
    return value;
  }
  try {
    const encoded = Array.from(value)
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join("");
    return decodeURIComponent(encoded);
  } catch {
    return value;
  }
};

// Componente interno para o card de tipologia com tratamento de imagem
const TipologiaCard: React.FC<{ tipologia: ITipologia; onNavigate: (id: number) => void }> = ({ tipologia, onNavigate }) => {
  const [imageError, setImageError] = useState(false);
  const croquiUrl = getCroquiUrl(tipologia);

  return (
    <Paper
      elevation={3}
      sx={{
        position: "relative",
        cursor: "pointer",
        transition: "all 0.3s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 6,
        },
      }}
      onClick={() => onNavigate(tipologia.idTipologia)}
    >
      {/* Borda verde no topo */}
      <Box
        sx={{
          height: "4px",
          backgroundColor: "#4caf50",
          width: "100%",
        }}
      />

      {/* Imagem/Desenho */}
      <Box
        sx={{
          position: "relative",
          p: 2,
          backgroundColor: "#f8f9fa",
          minHeight: "250px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px dashed #ddd",
          borderRadius: 1,
        }}
      >
        {croquiUrl && !imageError ? (
          <>
            <Box
              component="img"
              src={croquiUrl}
              alt={`Croqui ${tipologia.codigo}`}
              onError={() => setImageError(true)}
              sx={{
                width: "100%",
                maxHeight: "230px",
                objectFit: "contain",
              }}
            />
            {/* Ícone de lupa no canto superior direito */}
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: "50%",
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: 2,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                window.open(croquiUrl, "_blank");
              }}
            >
              <SearchIcon sx={{ fontSize: 18, color: "#3C90AD" }} />
            </Box>
          </>
        ) : (
          <Box
            sx={{
              color: "#ccc",
              textAlign: "center",
              py: 4,
            }}
          >
            <Typography variant="caption">Sem imagem</Typography>
          </Box>
        )}
      </Box>

      {/* Informações do Card */}
      <Box sx={{ p: 2, position: "relative" }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Código
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {tipologia.codigo}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Descrição
            </Typography>
            <Typography variant="body2">
              {tipologia.descricao}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Linha
            </Typography>
            <Typography variant="body2" fontWeight="500">
              {tipologia.linhaNome}
            </Typography>
          </Grid>
        </Grid>

        {/* Logo do Projetista no canto inferior direito */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              color: "#3C90AD",
              fontSize: "10px",
              textTransform: "uppercase",
            }}
          >
            {tipologia.projetistaNome}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export const Tipologias = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openNotification } = useNavigation();
  const [tipologias, setTipologias] = useState<ITipologia[]>([]);
  const [totalItemsPagina, setTotalItens] = useState<number>(0);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [viewMode, setViewMode] = useState<"lista" | "cards">("lista");
  const [isLoadingCadastros, setIsLoadingCadastros] = useState(true);
  const [acessoMensagem, setAcessoMensagem] = useState<string | null>(null);
  const [cadastrosN2, setCadastrosN2] = useState<ICadastrosN2 | null>(null);
  const [projetistasPermitidos, setProjetistasPermitidos] = useState<number[]>([]);
  const [projetistaOptions, setProjetistaOptions] = useState<IProjetista[]>([]);
  const [projetistaFiltro, setProjetistaFiltro] = useState<number | "all">("all");
  const [situacaoFiltro, setSituacaoFiltro] = useState<"disponivel" | "todas">("disponivel");
  const SITUACAO_DISPONIVEL_LEGACY = "Dispon��vel Mercado";
  const SITUACAO_DISPONIVEL_LABEL = "Disponível Mercado";
  const normalizarSituacao = (value?: string | null): string => {
    if (!value) return "";
    if (value.includes("Dispon")) {
      return SITUACAO_DISPONIVEL_LABEL;
    }
    return decodeLegacyText(value);
  };

  const [filterColumnsDebounced, setFilterColumnsDebounced] = useState<{
    column?: "codigo" | "descricao" | "linha";
    value?: string;
  }>();

  const isMasterUser = user?.role?.includes("Master");

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    sortBy: "codigo",
    direction: "asc",
    search: "",
    codigo: "",
    descricao: "",
    linha: "",
    projetistaId: undefined as number | undefined,
    linhaId: undefined as number | undefined,
    situacao: undefined as string | undefined,
    // Frente 8 (07/09/2026): a lista de Consultas é sempre "só disponibilizadas", para todo usuário.
    apenasDisponibilizadas: true,
    projetistasIds: undefined as number[] | undefined,
  });

  const loadProjetistasPermitidos = useCallback(
    async (projetistasIds: number[]) => {
      try {
        const todosProjetistas = await getProjetistas({ page: 1, pageSize: 1000 });
        const options = todosProjetistas.filter((projetista) =>
          projetistasIds.includes(projetista.idProjetista)
        );
        setProjetistaOptions(options);
      } catch (error) {
        openNotification({
          message: "Erro ao carregar projetistas habilitados",
          type: "error",
        });
      }
    },
    [openNotification]
  );

  useEffect(() => {
    const carregarCadastros = async () => {
      // Frente 8 do plano de 07/09/2026 (decisão do TI CEM, 07/09 14:37): a lista de Consultas
      // mostra só tipologias disponibilizadas ao mercado "independente de qual o usuário" —
      // Master deixou de ser exceção. O filtro é `apenasDisponibilizadas` (servidor), que também
      // mantém visível a tipologia já publicada cuja versão nova está em Validação/Homologação.
      if (isMasterUser) {
        setAcessoMensagem(null);
        setIsLoadingCadastros(false);
        setSituacaoFiltro("disponivel");
        setPagination((prev) => ({
          ...prev,
          situacao: undefined,
          apenasDisponibilizadas: true,
        }));
        setTipologias([]);
        setTotalItens(0);
        return;
      }

      if (!user?.idEmpresa) {
        setAcessoMensagem("Empresa não identificada para o usuário atual.");
        setIsLoadingCadastros(false);
        setTipologias([]);
        setTotalItens(0);
        return;
      }

      setIsLoadingCadastros(true);
      // Frente 1 (07/09): pela própria empresa do token — o endpoint por id exige Configuracao.
      const dadosCadastros = await getMeusCadastrosN2();

      if (!dadosCadastros || !dadosCadastros.acessoCadastroTipologiasLiberadas) {
        setAcessoMensagem(
          "O Cadastro de Tipologias não está habilitado para esta empresa. Ative o acesso em Cadastros N2."
        );
        setCadastrosN2(dadosCadastros);
        setIsLoadingCadastros(false);
        setTipologias([]);
        setTotalItens(0);
        return;
      }

      if (!dadosCadastros.projetistasCadastroTipologias?.length) {
        setAcessoMensagem(
          "Nenhum projetista habilitado para Tipologias. Configure os projetistas no Cadastro N2."
        );
        setCadastrosN2(dadosCadastros);
        setIsLoadingCadastros(false);
        setTipologias([]);
        setTotalItens(0);
        return;
      }

      setCadastrosN2(dadosCadastros);
      setProjetistasPermitidos(dadosCadastros.projetistasCadastroTipologias);
      setProjetistaFiltro("all");
      // Só tipologias disponibilizadas ao mercado (Frente 8, 07/09/2026) — o critério fica no
      // servidor (`apenasDisponibilizadas`), não no texto da situação.
      setSituacaoFiltro("disponivel");
      setPagination((prev) => ({
        ...prev,
        projetistasIds: dadosCadastros.projetistasCadastroTipologias,
        situacao: undefined,
        apenasDisponibilizadas: true,
        page: 1,
      }));
      await loadProjetistasPermitidos(dadosCadastros.projetistasCadastroTipologias);
      setAcessoMensagem(null);
      setIsLoadingCadastros(false);
    };

    carregarCadastros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.idEmpresa, isMasterUser, loadProjetistasPermitidos]);

  const debouncedFilterColumn = useCallback(
    debounce((column: "codigo" | "descricao" | "linha", value: string) => {
      setFilterColumnsDebounced({ column, value });
    }, TIME_DEBOUNCE),
    []
  );

  const columns: GridColDef[] = [
    {
      field: "urlDesenhoSvg",
      headerName: "Desenho",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const croquiUrl = getCroquiUrl(params.row as ITipologia);
        return (
        <div style={{ padding: "4px", textAlign: "center" }}>
            {croquiUrl ? (
            <Tooltip
              title={
                <Box
                  component="img"
                  src={croquiUrl}
                  alt={params.row.codigo}
                  sx={{
                    width: "230px",
                    maxHeight: "230px",
                    objectFit: "contain",
                    display: "block",
                    backgroundColor: "#fff",
                    borderRadius: 1,
                  }}
                />
              }
              arrow
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "transparent",
                    padding: 0,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    maxWidth: "none",
                  },
                },
                arrow: {
                  sx: {
                    color: "#fff",
                  },
                },
              }}
            >
              <img
                src={croquiUrl}
                alt={params.row.codigo}
                style={{ maxWidth: "100px", maxHeight: "50px", objectFit: "contain", cursor: "pointer" }}
                onError={(event) => {
                  const target = event.currentTarget;
                  target.style.display = "none";
                  const placeholder = target.parentElement;
                  if (placeholder) {
                    placeholder.innerHTML =
                      '<div style="color:#999;font-size:12px;">Sem imagem</div>';
                  }
                }}
              />
            </Tooltip>
          ) : (
            <div style={{ color: "#999", fontSize: "12px" }}>Sem imagem</div>
          )}
        </div>
        );
      },
    },
    {
      field: "codigo",
      headerName: "Código",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Código"
          name="codigo"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => debouncedFilterColumn("codigo", e.target.value)}
        />
      ),
    },
    {
      field: "descricao",
      headerName: "Descrição",
      flex: 2,
      minWidth: 250,
      renderHeader: () => (
        <FilterColumn
          label="Descrição"
          name="descricao"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => debouncedFilterColumn("descricao", e.target.value)}
        />
      ),
    },
    {
      field: "projetistaNome",
      headerName: "Projetista",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "linhaNome",
      headerName: "Linha",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Linha"
          name="linha"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedFilterColumn("linha", e.target.value)
          }
        />
      ),
    },
    {
      field: "dataUltimaAtualizacao",
      headerName: "Data Ult Atlz",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return new Date(value).toLocaleDateString("pt-BR");
      },
    },
    // Coluna de Ações temporariamente oculta - será reativada futuramente
    // {
    //   field: "acoes",
    //   headerName: "Ações",
    //   width: 100,
    //   sortable: false,
    //   renderCell: (params) => (
    //     <SearchIcon
    //       style={{ cursor: "pointer", color: "#3C90AD" }}
    //       onClick={() => navigate(`/consultaTipologiasMof/${params.row.idTipologia}`)}
    //     />
    //   ),
    // },
  ];

  const hasAcessoTipologias =
    isMasterUser ||
    (!isLoadingCadastros &&
      !acessoMensagem &&
      (!cadastrosN2 || projetistasPermitidos.length > 0));

  const aplicarNormalizacao = (lista: ITipologia[]): ITipologia[] =>
    lista.map((item) => ({
      ...item,
      descricao: decodeLegacyText(item.descricao),
      linhaNome: decodeLegacyText(item.linhaNome),
      situacao: normalizarSituacao(item.situacao),
      projetistaNome: decodeLegacyText(item.projetistaNome),
    }));

  const getTipologiasData = async (params: any) => {
    if (!hasAcessoTipologias) {
      setTipologias([]);
      setTotalItens(0);
      return;
    }

    const queryParams: Record<string, any> = {
      ...params,
    };

    if (!isMasterUser) {
      queryParams.projetistasIds =
        params?.projetistasIds ??
        (projetistasPermitidos.length > 0 ? projetistasPermitidos : undefined);
    } else {
      delete queryParams.projetistasIds;
    }

    if (!queryParams.situacao) {
      delete queryParams.situacao;
    }
    if (!queryParams.search) {
      delete queryParams.search;
    }
    if (!queryParams.codigo) {
      delete queryParams.codigo;
    }
    if (!queryParams.descricao) {
      delete queryParams.descricao;
    }
    if (!queryParams.linha) {
      delete queryParams.linha;
    }

    setIsLoadingData(true);
    try {
      const [data, totalRecords] = await Promise.all([
        getTipologias(queryParams),
        getDadosPaginacao({
          ...queryParams,
          situacao: queryParams.situacao,
        }),
      ]);
      setTipologias(aplicarNormalizacao(data));
      setTotalItens(totalRecords);
    } catch (error) {
      openNotification({
        message: "Erro ao carregar tipologias",
        type: "error",
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  const handlePagination = (updates: any) => {
    setPagination((prev) => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    if (!hasAcessoTipologias || !filterColumnsDebounced) return;

    const { column, value } = filterColumnsDebounced;
    const sanitizedValue = value ?? "";
    const updates: Record<string, any> = { page: 1 };

    if (column === "codigo") {
      updates.codigo = sanitizedValue;
    }
    if (column === "descricao") {
      updates.descricao = sanitizedValue;
    }
    if (column === "linha") {
      updates.linha = sanitizedValue;
    }

    handlePagination(updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterColumnsDebounced, hasAcessoTipologias]);

  useEffect(() => {
    if (!hasAcessoTipologias) return;
    getTipologiasData(pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.page,
    pagination.pageSize,
    pagination.search,
    pagination.codigo,
    pagination.descricao,
    pagination.linha,
    pagination.projetistasIds,
    pagination.situacao,
    hasAcessoTipologias,
]);

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: "lista" | "cards" | null
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  if (isLoadingCadastros) {
    return (
      <DashboardStackCustom>
        <NavigationHeader
          title="CADASTRO DE TIPOLOGIAS"
          label="Tipologias"
          hasBackButton={false}
        />
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Carregando configurações de acesso...
          </Typography>
        </Paper>
      </DashboardStackCustom>
    );
  }

  if (!isMasterUser && acessoMensagem) {
    return (
      <DashboardStackCustom>
        <NavigationHeader
          title="CADASTRO DE TIPOLOGIAS"
          label="Tipologias"
          hasBackButton={false}
        />
        <Paper sx={{ p: 4, backgroundColor: "#fff3cd", borderColor: "#ffeeba" }} variant="outlined">
          <Typography variant="body1" color="textPrimary">
            {acessoMensagem}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Ajuste as configurações em <strong>Empresas &gt; Cadastros N2</strong> para liberar o acesso.
          </Typography>
        </Paper>
      </DashboardStackCustom>
    );
  }

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title="CADASTRO DE TIPOLOGIAS"
        label="Tipologias"
        hasBackButton={false}
      />
      
      <Grid
        container
        display="flex"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Grid item xs={12} md={viewMode === "cards" ? 4 : 6}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="lista">
              <ViewList sx={{ mr: 1 }} />
              Lista
            </ToggleButton>
            <ToggleButton value="cards">
              <ViewModule sx={{ mr: 1 }} />
              Cards
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        
        {viewMode === "cards" && (
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Faça uma busca pelo código ou descrição"
              value={pagination.search}
              onChange={(e) => {
                handlePagination({ search: e.target.value, page: 1 });
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        )}

        {/* Campo Situação oculto - não existe no mockup */}
        <Grid item xs={12} md={viewMode === "cards" ? 4 : 6} sx={{ display: "none" }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Situação"
            value={situacaoFiltro}
            onChange={(event) => {
              const value = event.target.value as "disponivel" | "todas";
              setSituacaoFiltro(value);
              if (value === "disponivel") {
                handlePagination({
                  situacao: SITUACAO_DISPONIVEL_LABEL, // Usar o label correto
                  page: 1,
                });
              } else {
                handlePagination({
                  situacao: undefined,
                  page: 1,
                });
              }
            }}
          >
            <MenuItem value="disponivel">
              {SITUACAO_DISPONIVEL_LABEL}
            </MenuItem>
            <MenuItem value="todas">Ver todas</MenuItem>
          </TextField>
        </Grid>

        {!isMasterUser && projetistasPermitidos.length > 0 && (
          <Grid item xs={12} md={viewMode === "cards" ? 4 : 6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Projetista"
              value={projetistaFiltro}
              onChange={(event) => {
                const value = event.target.value as number | "all";
                setProjetistaFiltro(value);
                if (value === "all") {
                  handlePagination({
                    projetistasIds: cadastrosN2?.projetistasCadastroTipologias,
                    page: 1,
                  });
                } else {
                  handlePagination({
                    projetistasIds: [Number(value)],
                    page: 1,
                  });
                }
              }}
            >
              <MenuItem value="all">Todos os projetistas</MenuItem>
              {projetistaOptions.map((projetista) => (
                <MenuItem key={projetista.idProjetista} value={projetista.idProjetista}>
                  {projetista.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </Grid>

      {viewMode === "lista" ? (
        <Paper elevation={3}>
          <StackCustom>
            <TitleTable title="Listagem de tipologias" />
            <StripedDataGrid
              columns={columns}
              rows={tipologias}
              getRowId={(row) => row.idTipologia}
              loading={isLoadingData}
              page={pagination.page - 1}
              pageSize={pagination.pageSize}
              rowCount={totalItemsPagina}
              onPageChange={(newPage: number) => handlePagination({ page: newPage + 1 })}
              onPageSizeChange={(newPageSize: number) => {
                handlePagination({
                  pageSize: newPageSize,
                  page: 1,
                });
              }}
              checkboxSelection={false}
              sx={{
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
              }}
            />
          </StackCustom>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {isLoadingData ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, textAlign: "center" }}>
                Carregando tipologias...
              </Paper>
            </Grid>
          ) : tipologias.length === 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, textAlign: "center", color: "#999" }}>
                Nenhuma tipologia encontrada
              </Paper>
            </Grid>
          ) : (
            tipologias.map((tipologia) => (
              <Grid item xs={12} sm={6} md={4} key={tipologia.idTipologia}>
                <TipologiaCard
                  tipologia={tipologia}
                  onNavigate={(id) => navigate(`/consultaTipologiasMof/${id}`)}
                />
              </Grid>
            ))
          )}
          
          {/* Paginação para Cards */}
          {tipologias.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={Math.ceil(totalItemsPagina / pagination.pageSize)}
                  page={pagination.page}
                  onChange={(_event, newPage) => handlePagination({ page: newPage })}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </DashboardStackCustom>
  );
};

